import React, { useCallback, useRef, useState } from "react";

import IconButton from "@material-ui/core/IconButton";
import DotsIcon from "@material-ui/icons/MoreVert";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import ChangeTrainerPopup from "components/ChangeTrainerPopup";
import ActionsMenuPopover from "./ActionsMenuPopover";
import AddHorseToTrackPopup from "./AddHorseToTrackPopup";
import RemoveHorsePopup from "./RemoveHorsePopup";

import useStyles from "./styles";

interface IProps {
  horse: racehorse360.IHorse;
  refreshData: (horsePatch?: racehorse360.IHorse) => void;
}

const HorseActionsMenu = (props: IProps) => {
  const { horse, refreshData } = props;

  const classes = useStyles();
  const menuButtonRef = useRef<HTMLButtonElement>();

  const [isActionsMenuPopoverOpen, setIsActionsMenuPopoverOpen] =
    useState(false);
  const [isAddHorseToTrackPopupOpen, setIsAddHorseToTrackPopupOpen] =
    useState(false);
  const [isChangeTrainerPopupOpen, setIsChangeTrainerPopupOpen] =
    useState(false);
  const [isRemoveHorsePopupOpen, setIsRemoveHorsePopupOpen] = useState(false);

  const handleActionsMenuPopoverOpen = () => {
    setIsActionsMenuPopoverOpen(true);
  };

  const handleActionsMenuPopoverClose = () => {
    setIsActionsMenuPopoverOpen(false);
  };

  const handleAddHorseToTrackPopupOpen = () => {
    setIsAddHorseToTrackPopupOpen(true);
  };

  const handleCloseAddHorseToTrackPopup = useCallback(() => {
    setIsAddHorseToTrackPopupOpen(false);
  }, []);

  const handleAddHorseToTrack = useCallback(
    (horsePatch: racehorse360.IHorse) => {
      setIsAddHorseToTrackPopupOpen(false);
      refreshData(horsePatch);
    },
    []
  );

  const handleChangeTrainerPopupOpen = () => {
    setIsChangeTrainerPopupOpen(true);
  };

  const handleCloseChangeTrainerPopup = () => {
    setIsChangeTrainerPopupOpen(false);
  };

  const handleChangeTrainer = (horsePatch: racehorse360.IHorse) => {
    setIsChangeTrainerPopupOpen(false);
    refreshData(horsePatch);
  };

  const handleRemoveHorsePopupOpen = () => {
    setIsRemoveHorsePopupOpen(true);
  };

  const handleRemoveHorsePopupClose = (shouldRefreshData?: boolean) => {
    setIsRemoveHorsePopupOpen(false);
    shouldRefreshData && refreshData({ trainer: null });
  };

  const dotsIconClassName = isActionsMenuPopoverOpen
    ? classes.dotsIconActive
    : null;

  return (
    <>
      <IconButton
        key={"menuButton"}
        ref={menuButtonRef}
        size={"small"}
        onClick={handleActionsMenuPopoverOpen}
      >
        <DotsIcon className={dotsIconClassName} />
      </IconButton>

      {isActionsMenuPopoverOpen && (
        <ActionsMenuPopover
          menuButtonRef={menuButtonRef}
          currentHorse={horse}
          onClose={handleActionsMenuPopoverClose}
          onAddHorseToTrackPopupOpen={handleAddHorseToTrackPopupOpen}
          onChangeTrainerPopupOpen={handleChangeTrainerPopupOpen}
          onRemoveHorsePopupOpen={handleRemoveHorsePopupOpen}
        />
      )}

      {isAddHorseToTrackPopupOpen && (
        <AddHorseToTrackPopup
          open={true}
          onClose={handleCloseAddHorseToTrackPopup}
          onAddHorseToTrack={handleAddHorseToTrack}
          selectedHorse={horse}
        />
      )}

      {isChangeTrainerPopupOpen && (
        <ChangeTrainerPopup
          open={true}
          onClose={handleCloseChangeTrainerPopup}
          onChangeTrainer={handleChangeTrainer}
          selectedHorse={horse}
        />
      )}

      {isRemoveHorsePopupOpen && (
        <RemoveHorsePopup horse={horse} onClose={handleRemoveHorsePopupClose} />
      )}
    </>
  );
};

export default React.memo(HorseActionsMenu);
